/**
 * The left rail — the app's spine. It is on every wide screen, including the
 * flight list and the settings, because the pilot should never have to find
 * their way back to the rest of the app from inside one part of it.
 *
 * Below 1180 px it collapses to icons; the label stays as the accessible name
 * and the tooltip, so nothing becomes unreachable and nothing becomes a guess.
 */
import { NavLink, useLocation } from 'react-router-dom';
import { useT } from '../i18n';
import { useSession } from '../session';
import { Brand } from './Brand';
import {
  IconAccount, IconAircraft, IconFlights, IconSettings, IconSources,
} from './icons';

export type NavKey = 'flights' | 'aircraft' | 'settings' | 'sources' | 'account';

function Item({
  to,
  label,
  icon,
  on,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
  on: boolean;
}) {
  return (
    <NavLink className={'ae-rail-item' + (on ? ' on' : '')} to={to} title={label} aria-label={label}>
      {icon}
      <span className="ae-rail-label">{label}</span>
    </NavLink>
  );
}

export function Rail({ active }: { active?: NavKey | undefined }) {
  const t = useT();
  const session = useSession();
  const location = useLocation();

  return (
    <nav className="ae-rail" aria-label={t('nav.aria')}>
      <NavLink className="ae-rail-brand" to="/" aria-label="Aether">
        <Brand />
      </NavLink>
      <div className="ae-rail-nav">
        <Item to="/flights" label={t('nav.flights')} icon={<IconFlights />} on={active === 'flights'} />
        <Item to="/aircraft" label={t('nav.aircraft')} icon={<IconAircraft />} on={active === 'aircraft'} />
        <Item to="/settings" label={t('nav.settings')} icon={<IconSettings />} on={active === 'settings'} />
      </div>
      <div className="ae-rail-foot">
        {session.user ? (
          <Item to="/settings" label={session.user.email} icon={<IconAccount />} on={false} />
        ) : (
          <NavLink
            className="ae-rail-item"
            to="/login"
            state={{ from: location.pathname }}
            title={t('nav.signin')}
          >
            <IconAccount />
            <span className="ae-rail-label">{t('nav.signin')}</span>
          </NavLink>
        )}
        <Item to="/sources" label={t('nav.sources')} icon={<IconSources />} on={active === 'sources'} />
      </div>
    </nav>
  );
}
